import React from 'react';
import { AdminAction, AdminActionType } from 'types/interfaces/case';
import './CaseTimeline.css';

interface Props {
  actions: AdminAction[];
}

/**
 * Human labels for every action type the backend writes. Unknown values (a
 * newer backend) fall back to the raw name with underscores spaced out, so a
 * row is never dropped from the timeline.
 */
const ACTION_LABELS: Record<string, string> = {
  [AdminActionType.COMPLETE_TRADE]: 'Completed the trade',
  COMPLETE_NORMAL: 'Completed the trade',
  [AdminActionType.CANCEL_TRADE]: 'Cancelled the trade',
  CANCEL_NORMAL: 'Cancelled the trade',
  [AdminActionType.REFUND_BOTH]: 'Refunded both parties',
  [AdminActionType.NO_ACTION]: 'Resolved with no action',
  [AdminActionType.SET_DUE_DATE]: 'Set a due date',
  [AdminActionType.MESSAGE_SENT]: 'Sent a message',
  [AdminActionType.CASE_REOPENED]: 'Reopened the case',
  CASE_AUTO_CLOSED: 'Closed automatically (trade already settled)',
};

export const actionLabel = (actionType: string): string =>
  ACTION_LABELS[actionType] ?? actionType.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' });

const detailText = (details: AdminAction['details']): string | null => {
  if (!details) return null;
  if (typeof details.reason === 'string' && details.reason.trim()) return details.reason.trim();
  if (typeof details.summary === 'string' && details.summary.trim()) return details.summary.trim();
  return null;
};

/**
 * The case's admin history (Asana 1219265535165249): every resolution, due
 * date, message and reopen the backend recorded, oldest first. Read-only.
 */
const CaseTimeline: React.FC<Props> = ({ actions }) => {
  const ordered = [...actions].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  return (
    <div className="case-timeline" data-testid="case-timeline">
      <h2 className="panel-title">Case History</h2>
      {ordered.length === 0 ? (
        <p className="case-timeline__empty">No admin actions recorded yet.</p>
      ) : (
        <ol className="case-timeline__list">
          {ordered.map((action) => {
            const text = detailText(action.details);
            const source = action.details && typeof action.details.source === 'string' ? action.details.source : null;
            return (
              <li
                key={action.id}
                className={`case-timeline__item case-timeline__item--${action.actionType.toLowerCase()}`}
                data-action-type={action.actionType}
              >
                <div className="case-timeline__row">
                  <span className="case-timeline__label">{actionLabel(action.actionType)}</span>
                  <span className="case-timeline__who">
                    {action.adminUsername ? `@${action.adminUsername}` : source === 'SYSTEM' ? 'System' : 'Admin'}
                  </span>
                  <time className="case-timeline__when" dateTime={action.createdAt}>
                    {formatWhen(action.createdAt)}
                  </time>
                </div>
                {text && <p className="case-timeline__detail">{text}</p>}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default CaseTimeline;
